import { useState } from "react";
import { ChevronDown } from "lucide-react";

// Accept both object {q,a} and tuple [q,a] formats from data
function normalize(items) {
  if (!Array.isArray(items)) return [];
  return items
    .map((f) => {
      if (Array.isArray(f)) return { q: f[0], a: f[1] };
      if (f && typeof f === "object") return { q: f.q ?? f.question, a: f.a ?? f.answer };
      return null;
    })
    .filter((x) => x && x.q && x.a);
}

// Simple inline link parser for FAQ answers ([label](/url))
function renderInline(text) {
  if (!text || typeof text !== "string") return text;
  const parts = [];
  const regex = /\[([^\]]+)\]\(([^)]+)\)/g;
  let lastIndex = 0; let m; let k = 0;
  while ((m = regex.exec(text)) !== null) {
    if (m.index > lastIndex) parts.push(text.slice(lastIndex, m.index));
    const external = /^(https?:)?\/\//i.test(m[2]) || m[2].startsWith("mailto:") || m[2].startsWith("tel:");
    if (external) parts.push(<a key={`a-${k++}`} href={m[2]} target="_blank" rel="noreferrer" className="text-[#4F8B2A] underline underline-offset-2 hover:text-[#3F7A2E]">{m[1]}</a>);
    else parts.push(<a key={`a-${k++}`} href={m[2]} className="text-[#4F8B2A] underline underline-offset-2 hover:text-[#3F7A2E]">{m[1]}</a>);
    lastIndex = regex.lastIndex;
  }
  if (lastIndex < text.length) parts.push(text.slice(lastIndex));
  return parts.length ? parts : text;
}

export default function FAQ({ items, title = "Sık Sorulan Sorular" }) {
  const [open, setOpen] = useState(0);
  const list = normalize(items);
  if (list.length === 0) return null;
  return (
    <section data-testid="faq-section" className="py-20 md:py-24 bg-[#F5F7FA] border-y border-[#E5EAF0]">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <p className="chip mb-4 mx-auto">SSS</p>
          <h2 className="font-display text-3xl md:text-5xl text-[#0B1B33] tracking-tight">{title}</h2>
        </div>
        <div className="space-y-3">
          {list.map((f, i) => {
            const isOpen = open === i;
            return (
              <div
                key={i}
                data-testid={`faq-item-${i}`}
                className={`bg-white border rounded-2xl transition-all overflow-hidden ${
                  isOpen ? "border-[#10B981] shadow-md" : "border-[#E5EAF0]"
                }`}
              >
                <button
                  type="button"
                  onClick={() => setOpen(isOpen ? -1 : i)}
                  aria-expanded={isOpen}
                  aria-controls={`faq-panel-${i}`}
                  id={`faq-button-${i}`}
                  className="w-full flex items-center justify-between text-left px-5 md:px-7 py-5 md:py-6 gap-4"
                >
                  <span className="font-display text-base md:text-lg text-[#0B1B33] font-semibold leading-snug">{f.q}</span>
                  <span className={`shrink-0 h-8 w-8 rounded-full flex items-center justify-center transition-all ${isOpen ? "bg-[#10B981] text-white rotate-180" : "bg-[#F5F7FA] text-[#0B1B33]"}`}>
                    <ChevronDown size={16} />
                  </span>
                </button>
                <div
                  id={`faq-panel-${i}`}
                  role="region"
                  aria-labelledby={`faq-button-${i}`}
                  className="grid transition-all duration-300 ease-out"
                  style={{ gridTemplateRows: isOpen ? "1fr" : "0fr" }}
                >
                  <div className="overflow-hidden">
                    <p className="px-5 md:px-7 pb-6 text-[#4B5A6E] leading-relaxed text-[15px] whitespace-pre-line">{renderInline(f.a)}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

export function faqJsonLd(items) {
  const list = normalize(items);
  if (list.length === 0) return null;
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: list.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };
}
